import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { BookOpen, ArrowRight, Clock, Award } from "lucide-react";
import { Link } from "react-router-dom";

type ReviewStatus = 'draft' | 'submitted' | 'changes_requested' | 'approved' | 'rejected' | null;

interface TrainingProgressBannerProps {
  completedCourses: number;
  totalCourses: number;
  estimatedTimeRemaining?: string;
  reviewStatus?: ReviewStatus;
  className?: string;
}

// Get dynamic copy based on review status
function getTrainingCopy(reviewStatus: ReviewStatus, completedCourses: number, totalCourses: number) {
  const remaining = totalCourses - completedCourses;

  if (reviewStatus === 'approved') {
    return {
      title: 'Boost Your Visibility',
      description: `Complete ${remaining} more course${remaining !== 1 ? 's' : ''} to increase your chances of being discovered by clients`,
    };
  }

  if (reviewStatus === 'submitted') {
    return {
      title: 'Strengthen Your Application',
      description: `Completing your training improves your chances of profile approval. ${remaining} course${remaining !== 1 ? 's' : ''} remaining.`,
    };
  }

  if (reviewStatus === 'changes_requested') {
    return {
      title: 'Complete Required Training',
      description: `Training completion may be required for approval. Finish ${remaining} remaining course${remaining !== 1 ? 's' : ''}.`,
    };
  }

  // draft, rejected, or null - not yet submitted
  return {
    title: 'Required for Profile Submission',
    description: `You must complete all ${totalCourses} training courses before submitting your profile for review`,
  };
}

export function TrainingProgressBanner({
  completedCourses,
  totalCourses,
  estimatedTimeRemaining,
  reviewStatus,
  className,
}: TrainingProgressBannerProps) {
  const progress = totalCourses > 0
    ? Math.round((completedCourses / totalCourses) * 100)
    : 0;
  const isComplete = completedCourses >= totalCourses;

  if (isComplete) {
    return null;
  }

  const copy = getTrainingCopy(reviewStatus ?? null, completedCourses, totalCourses);

  return (
    <div
      className={cn("rounded-[40px] border-[3px] border-[rgba(245,158,11,0.60)] p-6", className)}
      style={{
        background: 'linear-gradient(99.87deg, rgba(245, 158, 11, 0.1) 0%, rgba(249, 115, 22, 0.1) 100%)',
      }}
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-2xl bg-white">
            <BookOpen className="h-6 w-6 text-amber-500" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              {copy.title}
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              {copy.description}
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 md:gap-8">
          {/* Progress Stats */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Award className="h-4 w-4 text-[rgb(245,158,11)]" />
              <span className="text-sm font-medium text-slate-900">
                {completedCourses}/{totalCourses} courses
              </span>
            </div>
            {estimatedTimeRemaining && (
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-[rgb(245,158,11)]" />
                <span className="text-sm text-slate-600">
                  ~{estimatedTimeRemaining} remaining
                </span>
              </div>
            )}
          </div>

          <Link to="/genius/courses">
            <Button
              className="bg-[rgb(245,158,11)] hover:bg-[rgb(217,119,6)] text-white rounded-full px-6"
            >
              Continue Training
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-slate-600">Training Progress</span>
          <span className="font-semibold text-[rgb(245,158,11)]">{progress}%</span>
        </div>
        <Progress value={progress} className="h-2 bg-[rgba(245,158,11,0.2)] [&>div]:bg-[rgb(245,158,11)]" />
      </div>
    </div>
  );
}
